/**
 * MissingHoursBanner — banner de status ore pentru dashboard și fișă persoană.
 *
 * Props:
 *   - scope: "dashboard" (afișează toate persoanele) | "person" (doar o persoană)
 *   - personId: string (dacă scope === "person")
 *
 * Comportament:
 * • Se preia lista de persoane așteptate să fi lucrat astăzi + statusul lor.
 * • După ora 17:00 devine roșu urgent pentru cei fără ore.
 * • Când persoana adaugă orele, rândul devine verde + arată cine + cât + la ce regie/șantier.
 * • Include zilele lipsă din ultimele 6 zile (istoric).
 * • Buton „→ Istoric ore" duce la fișa persoanei (calendar luna curentă).
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { fmtRoDate } from "../lib/dateFmt";

// „azi la 17:32" sau „11/08 la 17:32" pentru timestamp-ul introducerii orelor
function fmtAddedAt(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const hm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    const today = new Date();
    const sameDay = d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
    const dm = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
    return sameDay ? `azi la ${hm}` : `${dm} la ${hm}`;
  } catch {
    return null;
  }
}
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Clock, History, ChevronDown, ChevronUp, X, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { toast } from "sonner";

function isPastCutoff(iso) {
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  // Cutoff-ul de 17:00 se aplică DOAR pentru ziua curentă.
  // Pentru zile trecute, orice absență de ore = URGENT (roșu).
  if (iso && iso < today) return true;
  return now.getHours() >= 17;
}

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, days) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function MissingHoursBanner({ scope = "dashboard", personId = null }) {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showHistorical, setShowHistorical] = useState(false);
  const [alertsCount, setAlertsCount] = useState(0);
  const [viewDate, setViewDate] = useState(todayISO);

  const load = useCallback(async () => {
    try {
      const url = scope === "person"
        ? `/hours-status/person/${personId}`
        : `/hours-status/today${viewDate !== todayISO() ? `?date=${viewDate}` : ""}`;
      const { data: d } = await api.get(url);
      setData(d);
      if (scope === "dashboard" && viewDate === todayISO()) {
        try {
          const { data: al } = await api.get("/hour-edit-alerts?unread_only=true");
          setAlertsCount(al?.count || 0);
        } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  }, [scope, personId, viewDate]);

  useEffect(() => {
    load();
    // Reîmprospătează la fiecare 60s DOAR când te uiți la ziua curentă (nu are sens pentru zile trecute)
    if (viewDate !== todayISO()) return undefined;
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load, viewDate]);

  const markAlertsRead = async () => {
    try {
      await api.post("/hour-edit-alerts/mark-read");
      setAlertsCount(0);
      toast.success("Alerte marcate ca citite");
    } catch { /* ignore */ }
  };

  if (!data || dismissed) return null;

  // === Mode PERSON: doar zilele lipsă ===
  if (scope === "person") {
    const missing = data.missing_dates || [];
    if (missing.length === 0) return null;  // nimic de arătat
    const past = isPastCutoff();
    const bg = past ? "bg-red-50 border-red-500 text-red-900" : "bg-amber-50 border-amber-400 text-amber-900";
    return (
      <div className={`border-l-4 rounded-lg px-4 py-3 mb-3 shadow-sm ${bg}`} data-testid="missing-hours-person-banner">
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="flex-shrink-0 mt-0.5"/>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">
              {past ? "⚠️ LIPSESC ORE!" : "Ore incomplete"} —
              {" "}{missing.length} {missing.length === 1 ? "zi" : "zile"} fără ore înregistrate
            </div>
            <div className="text-xs mt-1 flex flex-wrap gap-1">
              {missing.map((d) => (
                <span key={d} className="bg-white/60 px-2 py-0.5 rounded font-mono border border-current/20">{fmtRoDate(d)}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // === Mode DASHBOARD: full banner ===
  const persons = data.persons || [];
  const historical = data.historical || [];
  const missing = persons.filter((p) => !p.logged);
  const logged = persons.filter((p) => p.logged);

  const isToday = viewDate === todayISO();
  const isPast = viewDate < todayISO();
  const isFuture = viewDate > todayISO();

  // Nu ascunde bannerul dacă utilizatorul navighează pe zile trecute — chiar dacă totul e OK,
  // trebuie să poată vedea statusul.
  if (persons.length === 0 && historical.length === 0 && alertsCount === 0 && isToday) return null;

  const past = isPastCutoff(viewDate);
  const allDone = missing.length === 0 && historical.length === 0;
  const bg = allDone
    ? "bg-emerald-50 border-emerald-500"
    : past && missing.length > 0
      ? "bg-red-50 border-red-500 animate-pulse-slow"
      : "bg-amber-50 border-amber-400";

  return (
    <div className={`border-l-4 rounded-lg p-4 mb-4 shadow-md ${bg}`} data-testid="missing-hours-dashboard-banner">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {allDone
            ? <CheckCircle2 size={26} className="text-emerald-600"/>
            : past
              ? <AlertTriangle size={26} className="text-red-600"/>
              : <Clock size={26} className="text-amber-600"/>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-black text-lg" data-testid="missing-hours-title">
              {allDone
                ? (isToday ? "✅ Toți angajații au logat orele astăzi" : "✅ Toți au logat orele")
                : past
                  ? (isToday ? "🚨 LIPSESC ORE! (după 17:00)" : "🚨 LIPSESC ORE!")
                  : "⏰ Status ore"}
            </h3>
            {/* Navigare între zile */}
            <div className="ml-auto inline-flex items-center gap-1 bg-white/70 rounded-md border border-current/20 px-1 py-0.5" data-testid="hours-day-nav">
              <button
                type="button"
                onClick={() => setViewDate((d) => addDays(d, -1))}
                className="p-1 hover:bg-white rounded transition-colors"
                title="Ziua anterioară"
                data-testid="hours-day-prev"
              >
                <ChevronLeft size={16}/>
              </button>
              <input
                type="date"
                value={viewDate}
                max={todayISO()}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v && /^\d{4}-\d{2}-\d{2}$/.test(v)) setViewDate(v);
                }}
                className="text-xs font-mono bg-transparent border-0 focus:outline-none px-1"
                data-testid="hours-day-picker"
              />
              <button
                type="button"
                onClick={() => setViewDate((d) => addDays(d, 1))}
                disabled={viewDate >= todayISO()}
                className="p-1 hover:bg-white rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title={viewDate >= todayISO() ? "Nu poți vedea în viitor" : "Ziua următoare"}
                data-testid="hours-day-next"
              >
                <ChevronRight size={16}/>
              </button>
              {!isToday && (
                <button
                  type="button"
                  onClick={() => setViewDate(todayISO())}
                  className="ml-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-blue-100 hover:bg-blue-200 text-blue-800 rounded border border-blue-300"
                  title="Revino la ziua de azi"
                  data-testid="hours-day-today"
                >
                  <CalendarDays size={10} className="inline mr-0.5"/> Azi
                </button>
              )}
            </div>
            <span className="text-xs text-zinc-500 font-mono w-full sm:w-auto" data-testid="hours-day-label">
              {fmtRoDate(data.today)}
              {isPast && <span className="ml-1 text-orange-700 font-bold">· ISTORIC</span>}
              {isFuture && <span className="ml-1 text-blue-700 font-bold">· VIITOR</span>}
            </span>
          </div>
          {!allDone && (
            <div className="text-sm mt-1">
              <strong className={past ? "text-red-700" : "text-amber-700"}>{missing.length}</strong> persoane fără ore
              {logged.length > 0 && (
                <>
                  {" · "}
                  <strong className="text-emerald-700">{logged.length}</strong> au logat deja
                  {" ("}
                  <strong className="text-emerald-700">{logged.filter((p) => p.report_uploaded).length}</strong> cu raport ·{" "}
                  <strong className="text-orange-700">{logged.filter((p) => !p.report_uploaded).length}</strong> fără raport{")"}
                </>
              )}
              {historical.length > 0 && (
                <>
                  {" · "}
                  <strong className="text-orange-700">{historical.length}</strong> cu zile lipsă și în trecut
                </>
              )}
            </div>
          )}
          {alertsCount > 0 && (
            <div className="mt-2 flex items-center gap-2 bg-orange-100 border border-orange-300 rounded px-2 py-1.5 text-xs">
              <span className="text-orange-800 font-bold">🔔 {alertsCount} {alertsCount === 1 ? "modificare" : "modificări"} de ore în afara săptămânii curente</span>
              <Link to="/cronologie?category=hours" className="ml-auto text-blue-700 hover:underline font-semibold" data-testid="alerts-cronologie-link">Vezi cronologie →</Link>
              <button type="button" onClick={markAlertsRead} className="text-zinc-600 hover:text-zinc-900" data-testid="alerts-mark-read">
                <X size={14}/>
              </button>
            </div>
          )}
          {(missing.length > 0 || logged.length > 0) && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setCollapsed((c) => !c)}
                className="text-xs font-semibold text-zinc-600 hover:text-zinc-900 flex items-center gap-1"
                data-testid="banner-toggle"
              >
                {collapsed ? <ChevronDown size={12}/> : <ChevronUp size={12}/>}
                {collapsed ? "Arată detalii" : "Ascunde detalii"}
              </button>
            </div>
          )}
          {!collapsed && persons.length > 0 && (
            <div className="mt-2 space-y-1 max-h-96 overflow-y-auto">
              {persons.map((p) => (
                <PersonStatusRow key={p.person_id} person={p} past={past}/>
              ))}
            </div>
          )}
          {historical.length > 0 && (
            <div className="mt-3 border-t border-current/20 pt-2">
              <button
                type="button"
                onClick={() => setShowHistorical((s) => !s)}
                className="text-xs font-semibold text-orange-700 hover:text-orange-900 flex items-center gap-1"
                data-testid="banner-historical-toggle"
              >
                <History size={12}/>
                {showHistorical ? "Ascunde" : "Arată"} zilele lipsă istoric ({historical.length})
              </button>
              {showHistorical && (
                <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                  {historical.map((h) => (
                    <div key={h.person_id} className="bg-orange-50 border border-orange-200 rounded px-2 py-1.5 text-xs flex items-start gap-2" data-testid={`hist-row-${h.person_id}`}>
                      <Link to={`/personal/${h.person_id}`} className="font-bold text-orange-900 hover:underline">{h.name}</Link>
                      <div className="flex-1 flex flex-wrap gap-1">
                        {h.missing_dates.map((d) => (
                          <span key={d} className="font-mono text-orange-800">{fmtRoDate(d)}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 text-zinc-400 hover:text-zinc-700"
          title="Închide bannerul (revine la reload)"
          data-testid="banner-dismiss"
        >
          <X size={18}/>
        </button>
      </div>
    </div>
  );
}

function PersonStatusRow({ person, past }) {
  const isLogged = person.logged;
  const bg = isLogged
    ? "bg-emerald-50 border-emerald-300"
    : past
      ? "bg-red-50 border-red-300"
      : "bg-amber-50 border-amber-300";
  const totalHours = person.logged_details.reduce((s, d) => s + (d.hours || 0), 0);
  return (
    <div className={`rounded border px-2 py-1.5 text-xs flex items-start gap-2 ${bg}`} data-testid={`row-${person.person_id}-${isLogged ? "ok" : "missing"}`}>
      <div className="flex-shrink-0 mt-0.5">
        {isLogged ? <CheckCircle2 size={14} className="text-emerald-600"/> : <AlertTriangle size={14} className={past ? "text-red-600" : "text-amber-600"}/>}
      </div>
      <Link to={`/personal/${person.person_id}`} className="font-bold hover:underline min-w-[140px]" data-testid={`row-name-${person.person_id}`}>
        {person.name}
      </Link>
      {isLogged && (
        person.report_uploaded ? (
          <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-900" title="Are raport de ore încărcat pentru această zi" data-testid={`report-ok-${person.person_id}`}>
            📎 cu raport
          </span>
        ) : (
          <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-200 text-orange-900" title="Orele sunt logate, dar NU există raport încărcat pentru această zi" data-testid={`report-missing-${person.person_id}`}>
            ⚠ fără raport
          </span>
        )
      )}
      <div className="flex-1 min-w-0">
        {isLogged ? (
          <div className="space-y-0.5">
            {person.logged_details.map((d, i) => (
              <div key={i} className="text-emerald-900">
                <strong>{d.hours}h</strong> @ <span className="italic">{d.label}</span>
                {d.actor_name && d.actor_name !== "—" && (
                  <span className="text-emerald-700/70"> · adăugat de <strong>{d.actor_name}</strong></span>
                )}
                {d.added_at && (
                  <span className="text-emerald-700/60 text-[11px]" data-testid={`added-at-${person.person_id}-${i}`}> · {fmtAddedAt(d.added_at)}</span>
                )}
              </div>
            ))}
            <div className="text-emerald-700 text-[10px] mt-0.5">
              Total: <strong>{totalHours}h</strong> ✓
            </div>
          </div>
        ) : (
          <div className={past ? "text-red-800" : "text-amber-800"}>
            Așteptat la: {person.expected.map((e) => e.label).join(", ") || "—"}
          </div>
        )}
      </div>
    </div>
  );
}
